import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import {
  Heart, Calendar, MessageCircle, TrendingUp, Megaphone, BarChart3, Eye,
  Newspaper, Image as ImageIcon, HandHeart, ArrowRight, Sparkles, MapPin, Clock,
  PlayCircle, Video as VideoIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Strip HTML tags from rich text for plain previews
const stripHtml = (html) => {
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

// Extract YouTube thumbnail from a video URL
const getYouTubeThumbnail = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};

export default function Home() {
  const [sliderImages, setSliderImages] = useState([]);
  const [news, setNews] = useState([]);
  const [latestObituary, setLatestObituary] = useState(null);
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [videos, setVideos] = useState([]);
  const [latestPoll, setLatestPoll] = useState(null);
  const [visitStats, setVisitStats] = useState({ total_visits: 0, today_visits: 0 });
  const [sliderInterval, setSliderInterval] = useState(5);
  const [carouselApi, setCarouselApi] = useState(null);

  // Auto-rotate slider based on admin-configured interval
  useEffect(() => {
    if (!carouselApi || sliderImages.length < 2) return;
    const intervalMs = Math.max(2, sliderInterval) * 1000;
    const timer = setInterval(() => {
      carouselApi.scrollNext();
    }, intervalMs);
    return () => clearInterval(timer);
  }, [carouselApi, sliderInterval, sliderImages.length]);

  useEffect(() => {
    const fetchJson = (path) => fetch(`${BACKEND_URL}${path}`).then(r => r.ok ? r.json() : null).catch(() => null);
    Promise.all([
      fetchJson('/api/slider'),
      fetchJson('/api/news'),
      fetchJson('/api/obituaries'),
      fetchJson('/api/events'),
      fetchJson('/api/announcements'),
      fetchJson('/api/gallery'),
      fetchJson('/api/polls'),
      fetchJson('/api/albums'),
      fetchJson('/api/videos'),
      fetchJson('/api/site-settings'),
    ]).then(([sl, ne, ob, ev, an, ga, po, ab, vi, st]) => {
      if (Array.isArray(sl)) setSliderImages(sl);
      if (Array.isArray(ne)) setNews(ne.slice(0, 4));
      if (Array.isArray(ob) && ob.length > 0) setLatestObituary(ob[0]);
      if (Array.isArray(ev)) setEvents(ev.slice(0, 3));
      if (Array.isArray(an)) setAnnouncements(an.slice(0, 3));
      if (Array.isArray(ga)) setGallery(ga.slice(0, 6));
      if (Array.isArray(po) && po.length > 0) setLatestPoll(po[0]);
      if (Array.isArray(ab)) {
        const sorted = [...ab].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setAlbums(sorted.slice(0, 3));
      }
      if (Array.isArray(vi)) {
        const sorted = [...vi].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setVideos(sorted.slice(0, 3));
      }
      if (st && typeof st.slider_interval === 'number') setSliderInterval(st.slider_interval);
    });

    // Visitor counter (sessionStorage dedup)
    const visited = sessionStorage.getItem('kiziloren_visited');
    if (!visited) {
      sessionStorage.setItem('kiziloren_visited', '1');
      fetch(`${BACKEND_URL}/api/visit`, { method: 'POST' })
        .then(r => r.json()).then(setVisitStats).catch(() => {});
    } else {
      fetch(`${BACKEND_URL}/api/stats`)
        .then(r => r.json()).then(setVisitStats).catch(() => {});
    }
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const formatShortDate = (dateString) => {
    if (!dateString) return { day: '', month: '' };
    const d = new Date(dateString);
    return {
      day: d.getDate(),
      month: d.toLocaleDateString('tr-TR', { month: 'short' }).toUpperCase().replace('.', ''),
    };
  };

  const quickAccess = [
    { to: '/haberler',     icon: Newspaper,    title: 'Haberler',    desc: 'Köyümüzden son haberler',     color: 'from-amber-500 to-orange-600' },
    { to: '/etkinlikler',  icon: Calendar,     title: 'Etkinlikler', desc: 'Yaklaşan etkinlikleri keşfet', color: 'from-rose-500 to-red-600' },
    { to: '/duyurular',    icon: Megaphone,    title: 'Duyurular',   desc: 'Önemli duyuru ve bildirimler', color: 'from-sky-500 to-blue-600' },
    { to: '/galeri',       icon: ImageIcon,    title: 'Foto Galeri', desc: 'Köyümüzden anılar',           color: 'from-emerald-500 to-green-600' },
    { to: '/bagis',        icon: HandHeart,    title: 'Bağış Yap',   desc: 'Topluma katkıda bulun',       color: 'from-fuchsia-500 to-pink-600' },
    { to: '/chat',         icon: MessageCircle,title: 'Sohbet Et',   desc: 'Üyelerle anlık iletişim',     color: 'from-violet-500 to-purple-600' },
  ];

  const featuredNews = news[0];
  const restNews = news.slice(1, 4);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/60 via-orange-50/40 to-white">
      {/* ────────────────────────────────────────────────────────────────── */}
      {/* HERO + SLIDER (immersive)                                         */}
      {/* ────────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Slider as background */}
        {sliderImages.length > 0 ? (
          <Carousel className="w-full" opts={{ loop: true }} plugins={[]} setApi={setCarouselApi}>
            <CarouselContent>
              {sliderImages.map((slide) => (
                <CarouselItem key={slide.id}>
                  <div className="relative h-[520px] sm:h-[600px] md:h-[680px]" data-testid={`slider-item-${slide.id}`}>
                    <img src={slide.image_url} alt={slide.title || ''} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-950/60 via-amber-900/40 to-amber-950/85" />
                    <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 text-[11px] sm:text-xs font-semibold tracking-wider uppercase rounded-full bg-white/15 text-amber-50 backdrop-blur-md border border-white/25" data-testid="welcome-badge">
                        <Sparkles className="w-3 h-3" /> Hoş Geldiniz
                      </span>
                      <h1 className="text-[36px] leading-[1.05] sm:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 drop-shadow-lg" style={{ fontFamily: 'Merriweather, serif' }}>
                        Kızılören Kasabası
                      </h1>
                      <p className="text-base sm:text-xl md:text-2xl text-amber-50/95 mb-7 sm:mb-9 max-w-2xl px-2">
                        {slide.description || 'Birlikte daha güçlüyüz, birlikte daha güzeliz'}
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 mb-7 sm:mb-9" data-testid="visitor-counter">
                        <div className="flex items-center gap-2 bg-white/15 backdrop-blur-md rounded-full px-4 sm:px-5 py-1.5 sm:py-2 border border-white/20">
                          <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-200" />
                          <span className="text-white text-xs sm:text-sm font-medium">Bugün: <strong>{visitStats.today_visits.toLocaleString('tr-TR')}</strong></span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/15 backdrop-blur-md rounded-full px-4 sm:px-5 py-1.5 sm:py-2 border border-white/20">
                          <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-200" />
                          <span className="text-white text-xs sm:text-sm font-medium">Toplam: <strong>{visitStats.total_visits.toLocaleString('tr-TR')}</strong></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-3 sm:left-6 bg-white/20 backdrop-blur-md border-white/30 text-white hover:bg-white hover:text-amber-800" />
            <CarouselNext className="right-3 sm:right-6 bg-white/20 backdrop-blur-md border-white/30 text-white hover:bg-white hover:text-amber-800" />
          </Carousel>
        ) : (
          // Fallback hero (no slider images)
          <div className="hero-section relative">
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 md:py-36 text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 text-[11px] sm:text-xs font-semibold tracking-wider uppercase rounded-full bg-white/15 text-amber-50 backdrop-blur-md border border-white/25">
                <Sparkles className="w-3 h-3" /> Hoş Geldiniz
              </span>
              <h1 className="text-[34px] leading-[1.1] sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
                Kızılören Kasabası
              </h1>
              <p className="text-base sm:text-xl md:text-2xl text-amber-50 mb-6 sm:mb-8 max-w-3xl mx-auto px-2">
                Birlikte daha güçlüyüz, birlikte daha güzeliz
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2.5 mb-7" data-testid="visitor-counter">
                <div className="flex items-center gap-2 bg-white/15 backdrop-blur-md rounded-full px-4 py-1.5 border border-white/20">
                  <Eye className="w-4 h-4 text-amber-200" />
                  <span className="text-white text-xs sm:text-sm font-medium">Bugün: <strong>{visitStats.today_visits.toLocaleString('tr-TR')}</strong></span>
                </div>
                <div className="flex items-center gap-2 bg-white/15 backdrop-blur-md rounded-full px-4 py-1.5 border border-white/20">
                  <TrendingUp className="w-4 h-4 text-amber-200" />
                  <span className="text-white text-xs sm:text-sm font-medium">Toplam: <strong>{visitStats.total_visits.toLocaleString('tr-TR')}</strong></span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* QUICK ACCESS TILES (Hızlı Erişim)                                 */}
      {/* ────────────────────────────────────────────────────────────────── */}
      <section className="relative -mt-14 sm:-mt-20 z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {quickAccess.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group relative bg-white rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-2xl border border-amber-100/80 hover:-translate-y-1 transition-all duration-300 active:scale-[0.98]"
              data-testid={`quick-${item.to.replace(/\//g, '')}`}
            >
              <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="mt-3 text-sm sm:text-[15px] font-bold text-amber-900">{item.title}</h3>
              <p className="hidden sm:block mt-1 text-xs text-amber-700/80 leading-snug">{item.desc}</p>
              <ArrowRight className="absolute top-4 right-4 w-4 h-4 text-amber-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          ))}
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* FEATURED NEWS                                                     */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="flex items-end justify-between mb-7 sm:mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2 text-amber-600">
                <div className="w-8 h-px bg-amber-500" />
                <span className="text-xs font-semibold tracking-wider uppercase">Güncel</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
                Son Haberler
              </h2>
            </div>
            <Link to="/haberler" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-orange-600 transition-colors group">
              Tümü <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
            {/* Featured big card */}
            {featuredNews && (
              <Link to={`/haberler/${featuredNews.id}`} className="group lg:col-span-2 relative rounded-3xl overflow-hidden bg-amber-900 min-h-[280px] sm:min-h-[420px] block" data-testid="featured-news">
                {featuredNews.image_url ? (
                  <img src={featuredNews.image_url} alt={featuredNews.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-600 to-orange-700" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
                  <span className="inline-block self-start px-2.5 py-1 mb-3 text-[10px] sm:text-xs font-bold tracking-wider uppercase rounded-full bg-amber-500 text-white">Öne Çıkan</span>
                  <h3 className="text-xl sm:text-3xl font-bold text-white mb-2 sm:mb-3 leading-snug" style={{ fontFamily: 'Merriweather, serif' }}>
                    {featuredNews.title}
                  </h3>
                  <p className="text-amber-100/90 line-clamp-2 text-sm sm:text-base mb-4">{stripHtml(featuredNews.content)}</p>
                  <div className="flex items-center gap-3 text-amber-200/90 text-xs sm:text-sm">
                    <span>{featuredNews.author}</span>
                    <span className="w-1 h-1 rounded-full bg-amber-300/60" />
                    <span>{formatDate(featuredNews.created_at)}</span>
                  </div>
                </div>
              </Link>
            )}

            {/* Smaller news list */}
            <div className="flex flex-col gap-4 sm:gap-5">
              {restNews.map((n) => (
                <Link key={n.id} to={`/haberler/${n.id}`} className="group flex gap-3 sm:gap-4 bg-white rounded-2xl p-3 border border-amber-100/80 hover:border-amber-300 hover:shadow-md transition-all" data-testid={`news-card-${n.id}`}>
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl overflow-hidden bg-amber-100">
                    {n.image_url ? (
                      <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-7 h-7 text-amber-400" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm sm:text-[15px] text-amber-900 line-clamp-2 group-hover:text-orange-600 transition-colors" style={{ fontFamily: 'Merriweather, serif' }}>
                      {n.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-2 text-xs text-amber-600">
                      <Clock className="w-3 h-3" />
                      {formatDate(n.created_at)}
                    </div>
                  </div>
                </Link>
              ))}
              {restNews.length === 0 && featuredNews && (
                <Link to="/haberler" className="flex items-center justify-center gap-2 rounded-2xl p-6 bg-amber-50 border border-dashed border-amber-200 text-amber-700 text-sm hover:bg-amber-100 transition-colors">
                  <ArrowRight className="w-4 h-4" /> Tüm haberleri görüntüle
                </Link>
              )}
            </div>
          </div>

          <div className="sm:hidden mt-5 text-center">
            <Link to="/haberler" className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-orange-600">
              Tüm Haberler <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* EVENTS + ANNOUNCEMENTS  (split, modern list)                       */}
      {/* ────────────────────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Events */}
          <div className="bg-white rounded-3xl border border-amber-100/80 shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-amber-100/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-sm">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>Yaklaşan Etkinlikler</h3>
                  <p className="text-xs text-amber-600">Topluluğumuzun yeni etkinlikleri</p>
                </div>
              </div>
              <Link to="/etkinlikler" className="text-xs sm:text-sm font-medium text-amber-700 hover:text-orange-600">Tümü</Link>
            </div>
            <div className="divide-y divide-amber-50">
              {events.length === 0 && (
                <p className="p-8 text-center text-sm text-amber-600">Henüz etkinlik bulunmuyor.</p>
              )}
              {events.map((ev) => {
                const d = formatShortDate(ev.date);
                return (
                  <Link key={ev.id} to="/etkinlikler" className="flex items-center gap-4 p-4 sm:p-5 hover:bg-amber-50/60 transition-colors group" data-testid={`event-row-${ev.id}`}>
                    <div className="flex-shrink-0 w-14 sm:w-16 text-center rounded-xl bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-200 py-2">
                      <div className="text-xl sm:text-2xl font-bold text-amber-700 leading-none">{d.day}</div>
                      <div className="text-[10px] sm:text-xs font-semibold text-amber-600 mt-0.5">{d.month}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm sm:text-[15px] text-amber-900 line-clamp-1 group-hover:text-orange-600">{ev.title}</h4>
                      <p className="text-xs sm:text-sm text-amber-700/80 line-clamp-1 mt-0.5">{stripHtml(ev.description)}</p>
                      {ev.location && (
                        <div className="flex items-center gap-1 mt-1.5 text-[11px] sm:text-xs text-amber-600">
                          <MapPin className="w-3 h-3" />{ev.location}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="hidden sm:block w-4 h-4 text-amber-400 group-hover:text-orange-600 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Announcements */}
          <div className="bg-white rounded-3xl border border-amber-100/80 shadow-sm overflow-hidden">
            <div className="p-5 sm:p-6 border-b border-amber-100/70 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-sm">
                  <Megaphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>Son Duyurular</h3>
                  <p className="text-xs text-amber-600">Dernekten önemli bildirimler</p>
                </div>
              </div>
              <Link to="/duyurular" className="text-xs sm:text-sm font-medium text-amber-700 hover:text-orange-600">Tümü</Link>
            </div>
            <div className="divide-y divide-amber-50">
              {announcements.length === 0 && (
                <p className="p-8 text-center text-sm text-amber-600">Henüz duyuru bulunmuyor.</p>
              )}
              {announcements.map((an) => (
                <Link key={an.id} to="/duyurular" className="flex items-start gap-3 p-4 sm:p-5 hover:bg-amber-50/60 transition-colors group" data-testid={`ann-row-${an.id}`}>
                  <div className="flex-shrink-0 mt-1 w-2 h-2 rounded-full bg-sky-500" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm sm:text-[15px] text-amber-900 line-clamp-1 group-hover:text-orange-600">{an.title}</h4>
                    <p className="text-xs sm:text-sm text-amber-700/80 line-clamp-2 mt-1">{stripHtml(an.content)}</p>
                    <div className="flex items-center gap-1 mt-1.5 text-[11px] sm:text-xs text-amber-600">
                      <Clock className="w-3 h-3" />{formatDate(an.created_at)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* OBITUARY + POLL row                                                */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {(latestObituary || latestPoll) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {latestObituary && (
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-stone-700 via-stone-800 to-stone-900 text-stone-100 p-6 sm:p-8" data-testid="latest-obituary">
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
                  backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)',
                  backgroundSize: '20px 20px',
                }} />
                <div className="relative flex items-center gap-4 sm:gap-5">
                  {latestObituary.image_url ? (
                    <img src={latestObituary.image_url} alt={`${latestObituary.first_name} ${latestObituary.last_name}`} className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-stone-500/50 shadow-lg flex-shrink-0" />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-stone-600 flex items-center justify-center text-2xl sm:text-3xl font-bold flex-shrink-0">
                      {latestObituary.first_name?.[0]}{latestObituary.last_name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-stone-400 text-[11px] sm:text-xs font-semibold tracking-wider uppercase mb-1">Vefat Eden</p>
                    <h3 className="text-lg sm:text-2xl font-bold mb-1.5 leading-tight" style={{ fontFamily: 'Merriweather, serif' }}>
                      {latestObituary.first_name} {latestObituary.last_name}
                    </h3>
                    <p className="text-stone-300 text-xs sm:text-sm italic">&ldquo;Mekânı cennet olsun&rdquo;</p>
                    <p className="text-stone-400 text-[11px] sm:text-xs mt-1.5">{formatDate(latestObituary.death_date)}</p>
                  </div>
                </div>
                <Link to="/vefat-edenler" className="relative mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-stone-200 hover:text-white border-b border-stone-500/50 hover:border-white pb-0.5 transition-colors" data-testid="view-obituaries-btn">
                  Tüm vefat edenler <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}

            {latestPoll && (
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-amber-100 via-orange-50 to-amber-50 border border-amber-200/80 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold tracking-wider uppercase text-amber-700">Güncel Anket</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-amber-900 mb-4 leading-snug" style={{ fontFamily: 'Merriweather, serif' }}>
                  {latestPoll.title}
                </h3>
                <div className="space-y-2 mb-5">
                  {latestPoll.options.slice(0, 3).map((option, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-white/70 backdrop-blur-sm rounded-lg border border-amber-200/60 text-sm text-amber-900">
                      <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="truncate">{option}</span>
                    </div>
                  ))}
                  {latestPoll.options.length > 3 && (
                    <p className="text-xs text-amber-600 pl-1">+ {latestPoll.options.length - 3} seçenek daha</p>
                  )}
                </div>
                <Link to="/anketler">
                  <Button size="sm" className="bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700 rounded-full">
                    Oy Ver <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* LATEST ALBUMS (Foto Galeriden)                                     */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {albums.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
          <div className="flex items-end justify-between mb-7 sm:mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2 text-amber-600">
                <div className="w-8 h-px bg-amber-500" />
                <span className="text-xs font-semibold tracking-wider uppercase">Anılar</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
                Foto Galeriden
              </h2>
              <p className="text-sm sm:text-base text-amber-700 mt-1">Son eklenen albümler</p>
            </div>
            <Link to="/galeri" className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-orange-600 group">
              Tüm Albümler <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {albums.map((a) => (
              <Link
                key={a.id}
                to={`/galeri/${a.slug}`}
                className="group relative rounded-3xl overflow-hidden bg-white shadow-sm border border-amber-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`home-album-${a.slug}`}
              >
                <div className="relative h-56 sm:h-64 overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100">
                  {a.cover_image ? (
                    <img src={a.cover_image} alt={a.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-12 h-12 text-amber-300" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm text-xs font-semibold text-amber-800 shadow-sm">
                    {a.photo_count || 0} fotoğraf
                  </div>
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-rose-600 text-xs font-semibold shadow-sm">
                    <Heart className="w-3.5 h-3.5" />
                    <span>{a.like_count || 0}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-0.5" style={{ fontFamily: 'Merriweather, serif' }}>
                      {a.name}
                    </h3>
                    {a.description && (
                      <p className="text-amber-50/90 text-xs sm:text-sm line-clamp-1">{a.description}</p>
                    )}
                  </div>
                </div>
                <div className="px-5 py-3 flex items-center justify-between text-amber-700 text-sm font-medium">
                  <span>Albümü Aç</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* LATEST VIDEOS                                                      */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {videos.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 sm:pb-20">
          <div className="flex items-end justify-between mb-7 sm:mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2 text-amber-600">
                <div className="w-8 h-px bg-amber-500" />
                <span className="text-xs font-semibold tracking-wider uppercase">İzle</span>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-amber-900" style={{ fontFamily: 'Merriweather, serif' }}>
                Video Galeri
              </h2>
              <p className="text-sm sm:text-base text-amber-700 mt-1">Son eklenen videolar</p>
            </div>
            <Link to="/video-galeri" className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-orange-600 group">
              Tüm Videolar <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {videos.map((v) => {
              const thumb = v.thumbnail_url || getYouTubeThumbnail(v.url);
              return (
                <Link
                  key={v.id}
                  to="/video-galeri"
                  className="group relative rounded-3xl overflow-hidden bg-amber-900 shadow-sm border border-amber-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                  data-testid={`home-video-${v.id}`}
                >
                  <div className="relative aspect-video overflow-hidden">
                    {thumb ? (
                      <img src={thumb} alt={v.title || ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-700 to-orange-800">
                        <VideoIcon className="w-14 h-14 text-amber-200" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    {/* Play icon */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 group-hover:bg-amber-500 transition-all">
                        <PlayCircle className="w-10 h-10 sm:w-12 sm:h-12 text-amber-700 group-hover:text-white transition-colors" fill="currentColor" strokeWidth={1.5} />
                      </div>
                    </div>
                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                      <h3 className="text-base sm:text-lg font-bold text-white line-clamp-2" style={{ fontFamily: 'Merriweather, serif' }}>
                        {v.title || 'İsimsiz Video'}
                      </h3>
                      {v.description && (
                        <p className="text-amber-50/90 text-xs sm:text-sm line-clamp-1 mt-1">{stripHtml(v.description)}</p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* CTA: BİZE KATILIN                                                  */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {/* FOOTER                                                             */}
      {/* ────────────────────────────────────────────────────────────────── */}
      <footer className="bg-amber-950 text-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-2.5 mb-4">
              <img
                src="/logo-128.png"
                alt="Kızılören Kasabası logosu"
                className="w-10 h-10 rounded-xl object-cover shadow-md"
              />
              <h3 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
                Kızılören Kasabası
              </h3>
            </div>
            <p className="text-amber-200/80 text-sm sm:text-base mb-4">
              Birlikte güçlü, birlikte dayanışma içinde
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 mb-5 text-xs sm:text-sm">
              <Link to="/hakkimizda" className="text-amber-200 hover:text-white transition-colors">Hakkımızda</Link>
              <span className="w-1 h-1 rounded-full bg-amber-700" />
              <Link to="/iletisim" className="text-amber-200 hover:text-white transition-colors">İletişim</Link>
            </div>
            <p className="text-amber-400/70 text-xs sm:text-sm">
              © 2026 Kızılören. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
